// src/data/friendshipTips.js - Daily Friendship Tips Database

export const FRIENDSHIP_TIPS = [
  // Brain Science Tips (20 tips)
  {
    id: 1,
    category: "science",
    title: "Brain Science",
    tip: "In early brain-scan studies, being left out of a game showed up in some of the same areas involved in physical pain. Later studies found the overlap is smaller than first thought, but being left out still genuinely hurts.",
    action: "Next time you feel hurt by a friend, be as gentle with yourself as you would be with a bruise. Module 1 has the research.",
    relatedModule: 1,
  },
  {
    id: 2,
    category: "science",
    title: "Brain Fact",
    tip: "Your brain runs social pain through some of the same circuitry as physical pain. Being left out genuinely hurts - that is not you being dramatic.",
    action: "Physical comfort (a warm drink, a cosy blanket, a hot shower) can take the edge off emotional pain, because your brain processes the two in overlapping ways.",
    relatedModule: 1,
  },
  {
    id: 3,
    category: "science",
    title: "Social Brain",
    tip: "Studies suggest teenagers feel being left out more strongly than adults do. Intense friendship feelings at your age are normal, not dramatic.",
    action: "Cut yourself some slack when friendship stuff feels huge. Module 1 explains the research.",
    relatedModule: 1,
  },
  {
    id: 4,
    category: "science",
    title: "Group Size",
    tip: "There's no right number of close friends. Some people have one, some have five, and it changes over the years.",
    action: "If your group feels overwhelming, it might just be too big for you right now. Smaller circles are totally normal.",
    relatedModule: 2,
  },
  {
    id: 5,
    category: "science",
    title: "Catching Moods",
    tip: "Moods can rub off on people: after time with someone who's stressed or low, you might feel it too.",
    action: "Feeling flat after time with a friend? It might be their week, not you, and not them as a person.",
    relatedModule: 2,
  },
  {
    id: 6,
    category: "science",
    title: "Conflict Chemistry",
    tip: "During conflict, your body releases cortisol and adrenaline - the same chemicals as physical danger.",
    action: "No wonder arguments feel so intense! Deep breathing can help reset your nervous system.",
    relatedModule: 3,
  },
  {
    id: 7,
    category: "science",
    title: "Digital Dopamine",
    tip: "Notifications are designed to grab your attention, and they're good at it.",
    action: "Turn off the ones you don't need. It's easier to decide when to look if your phone isn't deciding for you.",
    relatedModule: 4,
  },
  {
    id: 8,
    category: "science",
    title: "Grief Response",
    tip: "Losing a friend can hurt as much as a breakup, even when nobody around you treats it as a real loss.",
    action: "If a friendship ending hurts deeply, you're not overreacting. Module 5 is about exactly this.",
    relatedModule: 5,
  },
  {
    id: 9,
    category: "science",
    title: "Belonging Drive",
    tip: "Humans have a fundamental need to belong - it's not weakness, it's evolution ensuring our survival.",
    action: "Wanting friends isn't needy, it's human. Your brain is designed for connection.",
    relatedModule: 6,
  },
  {
    id: 10,
    category: "science",
    title: "Standing Tall",
    tip: "'Power posing' was a famous idea: stand like a superhero for two minutes and your hormones change. Bigger, later studies found no hormone change, and one of the original researchers said she no longer believed it.",
    action: "If standing tall helps you feel steadier before walking in somewhere, go for it. Just don't expect it to do the work for you. Module 1 has the story.",
    relatedModule: 7,
  },
  {
    id: 11,
    category: "science",
    title: "Amygdala Hijack",
    tip: "When you're upset, your amygdala (fear center) can override your prefrontal cortex (logic center) in milliseconds.",
    action: "Count to 10 before responding in conflict. It gives your logical brain time to catch up.",
    relatedModule: 3,
  },
  {
    id: 12,
    category: "science",
    title: "Social Comparison",
    tip: "Your brain makes constant social comparisons - most of them unconsciously.",
    action: "Notice when you're comparing. Ask: 'Is this comparison helping or hurting me?'",
    relatedModule: 4,
  },
  {
    id: 13,
    category: "science",
    title: "Attachment Patterns",
    tip: "The way you do friendships isn't fixed. It's shaped by what you've been through, and it can change.",
    action: "Noticing your patterns is the first step to changing the ones you don't like.",
    relatedModule: 8,
  },
  {
    id: 14,
    category: "science",
    title: "Stress Contagion",
    tip: "Stress can be catching: being around someone who's stressed can leave you feeling tense too.",
    action: "After time with stressed friends, do a reset: walk, breathe, shake it off (literally).",
    relatedModule: 2,
  },
  {
    id: 15,
    category: "science",
    title: "Vulnerability Paradox",
    tip: "Being vulnerable activates trust chemicals (oxytocin) in others, making them more likely to open up too.",
    action: "Share something small but real today. Vulnerability is magnetic.",
    relatedModule: 7,
  },
  {
    id: 16,
    category: "science",
    title: "Time Investment",
    tip: "One study of adults found it took about 50 hours together to go from acquaintance to casual friend, and around 200 hours to become close friends.",
    action: "Be patient with new friendships. Closeness takes time, and lots of ordinary time together. Module 6 has the research.",
    relatedModule: 6,
  },
  {
    id: 17,
    category: "science",
    title: "Conflict Memory",
    tip: "Bad moments with friends tend to stick in your memory more than good ones. Psychologists call it negativity bias.",
    action: "When a bad moment is on repeat, try recalling a few good moments with that friend too.",
    relatedModule: 3,
  },
  {
    id: 18,
    category: "science",
    title: "Digital Fatigue",
    tip: "Video calls are exhausting because your brain works harder to read faces on screens vs. in person.",
    action: "After video hangouts, give yourself a 15-minute screen break to recharge.",
    relatedModule: 4,
  },
  {
    id: 19,
    category: "science",
    title: "Grief Isn't a Checklist",
    tip: "Grief over a friendship doesn't follow a neat set of stages. Feelings come and go, often out of order.",
    action: "Wherever you are with it is OK. Healing isn't a straight line. Module 5 has more.",
    relatedModule: 5,
  },
  {
    id: 20,
    category: "science",
    title: "Self-Talk",
    tip: "The way you talk to yourself after a hard day with friends matters.",
    action: "Talk to yourself like you'd talk to your best friend.",
    relatedModule: 7,
  },

  // Quick Practices (30 tips)
  {
    id: 21,
    category: "practice",
    title: "Try This Today",
    tip: "The 48-hour rule: Wait 48 hours before responding to friendship drama.",
    action: "If something's bothering you, write it down but don't send it yet. Sleep on it twice.",
    relatedModule: 1,
  },
  {
    id: 22,
    category: "practice",
    title: "Quick Practice",
    tip: "The 5-4-3-2-1 grounding technique works for social anxiety too.",
    action: "Name 5 things you see, 4 you hear, 3 you feel, 2 you smell, 1 you taste. It can help bring you back to the room.",
    relatedModule: 1,
  },
  {
    id: 23,
    category: "practice",
    title: "Micro-Skill",
    tip: "The 'third thing' technique: After small talk, share a third personal detail to deepen connection.",
    action: "Move from weather → weekend plans → something you're genuinely excited/worried about.",
    relatedModule: 6,
  },
  {
    id: 24,
    category: "practice",
    title: "Daily Practice",
    tip: "Notice the part you tend to play in each group you're in: the planner, the joker, the listener. It's often different in different groups.",
    action: "Notice where you feel most like yourself.",
    relatedModule: 2,
  },
  {
    id: 25,
    category: "practice",
    title: "Boundary Builder",
    tip: "Practice the phrase: 'I need to think about that and get back to you.'",
    action: "Use it once today instead of immediately saying yes to something you're unsure about.",
    relatedModule: 2,
  },
  {
    id: 26,
    category: "practice",
    title: "Conflict Tool",
    tip: "The XYZ formula: 'When you do X in situation Y, I feel Z.'",
    action: "Practice with something small: 'When you're late to plans, I feel unimportant.'",
    relatedModule: 3,
  },
  {
    id: 27,
    category: "practice",
    title: "Digital Detox",
    tip: "Set 'phone-free zones': meals, first hour after waking, last hour before bed.",
    action: "Pick one zone to implement today. Start small.",
    relatedModule: 4,
  },
  {
    id: 28,
    category: "practice",
    title: "Letting Go",
    tip: "Write a goodbye letter to a friendship that ended. You don't have to send it.",
    action: "Getting it out of your head and onto paper creates closure.",
    relatedModule: 5,
  },
  {
    id: 29,
    category: "practice",
    title: "Friend Finder",
    tip: "The 'say yes' experiment: Accept every social invitation for one week (that's safe and reasonable).",
    action: "You never know which 'meh' event might lead to an amazing friend.",
    relatedModule: 6,
  },
  {
    id: 30,
    category: "practice",
    title: "Confidence Hack",
    tip: "Create a 'hype playlist' of songs that make you feel unstoppable.",
    action: "Listen to it before social events. Music can shift your mood.",
    relatedModule: 7,
  },
  {
    id: 31,
    category: "practice",
    title: "Energy Check",
    tip: "Before saying yes to plans, ask: 'Will this fill or drain my social battery?'",
    action: "It's okay to say no to draining things, even with friends you love.",
    relatedModule: 1,
  },
  {
    id: 32,
    category: "practice",
    title: "Group Awareness",
    tip: "Observe unspoken group rules: Who can interrupt? What topics are taboo? Who has most influence?",
    action: "Just notice for now. Awareness is the first step to navigating dynamics.",
    relatedModule: 2,
  },
  {
    id: 33,
    category: "practice",
    title: "Repair Attempt",
    tip: "The magic words: 'I messed up. I'm sorry. How can I make it right?'",
    action: "If you've hurt someone, these three sentences can start healing.",
    relatedModule: 3,
  },
  {
    id: 34,
    category: "practice",
    title: "Screenshot Test",
    tip: "Before posting online, imagine it as a screenshot sent to your family. Still comfortable?",
    action: "It won't stop every bit of drama, but it's a quick check that can save you a regret.",
    relatedModule: 4,
  },
  {
    id: 35,
    category: "practice",
    title: "Closure Ritual",
    tip: "Create a small ending: write a letter you don't send and put it away somewhere, plant something, or move old photos into a private folder.",
    action: "Rituals can help you mark an ending and make space for what comes next.",
    relatedModule: 5,
  },
  {
    id: 36,
    category: "practice",
    title: "Conversation Starter",
    tip: "Ask 'What's been the best part of your week?' instead of 'How are you?'",
    action: "You'll get real answers and deeper conversations.",
    relatedModule: 6,
  },
  {
    id: 37,
    category: "practice",
    title: "Body Language",
    tip: "Open body language (uncrossed arms, facing toward person, nodding) makes you more approachable.",
    action: "Try it in your next conversation and notice the difference.",
    relatedModule: 7,
  },
  {
    id: 38,
    category: "practice",
    title: "Appreciation Text",
    tip: "Send one 'I appreciate you because...' text to a friend today.",
    action: "Be specific: 'I appreciate how you always remember what I tell you about my family.'",
    relatedModule: 8,
  },
  {
    id: 39,
    category: "practice",
    title: "Active Listening",
    tip: "Repeat back what you heard: 'So what you're saying is...'",
    action: "People feel truly heard when you reflect their words. Try it once today.",
    relatedModule: 3,
  },
  {
    id: 40,
    category: "practice",
    title: "Energy Check",
    tip: "Notice which times with friends leave you feeling lighter, and which leave you tired. It's about the situation as much as the person.",
    action: "Tired doesn't mean someone is bad for you. It might mean you need a different kind of time together, or more rest.",
    relatedModule: 1,
  },
  {
    id: 41,
    category: "practice",
    title: "Small Gesture",
    tip: "Share a meme, article, or song that made you think of a friend.",
    action: "'This made me think of you' = 'You matter to me' in modern friendship language.",
    relatedModule: 8,
  },
  {
    id: 42,
    category: "practice",
    title: "Difficult Convo",
    tip: "Start hard conversations with 'I want to talk about something difficult, but I value our friendship.'",
    action: "This frames it as care, not attack.",
    relatedModule: 3,
  },
  {
    id: 43,
    category: "practice",
    title: "Digital Boundary",
    tip: "You don't have to reply the second a message arrives.",
    action: "Reply when you're ready. If it helps, tell your close friends how you usually reply, so a slow reply doesn't read as a message in itself.",
    relatedModule: 4,
  },
  {
    id: 44,
    category: "practice",
    title: "Reverse Golden Rule",
    tip: "Treat friends how THEY want to be treated, not how you want to be treated.",
    action: "Some friends want advice, others just want to vent. Ask which they need.",
    relatedModule: 8,
  },
  {
    id: 45,
    category: "practice",
    title: "Weekly Check-in",
    tip: "Schedule one 10-minute call with a friend instead of endless texting.",
    action: "Hearing someone's voice often feels more connecting than texting.",
    relatedModule: 6,
  },
  {
    id: 46,
    category: "practice",
    title: "Compliment Specifically",
    tip: "Instead of 'You're great,' try 'I love how you make everyone feel included.'",
    action: "Specific compliments feel more genuine and meaningful.",
    relatedModule: 7,
  },
  {
    id: 47,
    category: "practice",
    title: "Pattern Breaking",
    tip: "If you're always the one making plans, say so, kindly: 'I feel like I'm usually the one who asks. Want to plan the next one?'",
    action: "Going quiet to see if they notice tends to leave you both hurt (Module 5 explains why). Saying it out loud gives the friendship a chance.",
    relatedModule: 8,
  },
  {
    id: 48,
    category: "practice",
    title: "Forgiveness Letter",
    tip: "Write what you'd want to hear from someone who hurt you. Then give that to yourself.",
    action: "Sometimes the closure we need, we have to create ourselves.",
    relatedModule: 5,
  },
  {
    id: 49,
    category: "practice",
    title: "Fun Injection",
    tip: "Suggest doing something new together instead of the usual hangout.",
    action: "Doing something new together gives you something to talk about for weeks. Try mini golf, a hike, or cooking something together.",
    relatedModule: 6,
  },
  {
    id: 50,
    category: "practice",
    title: "Silence Practice",
    tip: "In conversation, pause 3 seconds before responding.",
    action: "A short pause gives you time to actually listen before you answer.",
    relatedModule: 7,
  },

  // Reflection Prompts (20 tips)
  {
    id: 51,
    category: "reflection",
    title: "Ask Yourself",
    tip: "What friendship pattern keeps repeating in my life?",
    action: "Journal for 5 minutes about what you notice. Patterns reveal what needs healing.",
    relatedModule: 8,
  },
  {
    id: 52,
    category: "reflection",
    title: "Deep Question",
    tip: "Am I staying in this friendship out of loyalty or out of habit?",
    action: "There's a difference. Loyalty is active choice; habit is passive comfort.",
    relatedModule: 5,
  },
  {
    id: 53,
    category: "reflection",
    title: "Self-Check",
    tip: "Do I feel more 'me' or less 'me' around this person?",
    action: "Good friends make you feel more yourself, not less.",
    relatedModule: 6,
  },
  {
    id: 54,
    category: "reflection",
    title: "Honest Moment",
    tip: "What am I pretending not to know about this friendship?",
    action: "We often know the truth before we're ready to admit it.",
    relatedModule: 8,
  },
  {
    id: 55,
    category: "reflection",
    title: "Values Check",
    tip: "What do I actually value in friendship: depth, fun, loyalty, growth, support?",
    action: "Knowing what you value helps you recognise it (or its absence).",
    relatedModule: 6,
  },
  {
    id: 56,
    category: "reflection",
    title: "Energy Question",
    tip: "After hanging out, do I feel recharged or depleted?",
    action: "Your body knows before your mind does. Listen to it.",
    relatedModule: 1,
  },
  {
    id: 57,
    category: "reflection",
    title: "Conflict Style",
    tip: "Do I avoid conflict, escalate it, or navigate it?",
    action: "Your pattern isn't permanent. Awareness is the first step to changing it.",
    relatedModule: 3,
  },
  {
    id: 58,
    category: "reflection",
    title: "Digital Mirror",
    tip: "What version of myself am I performing online?",
    action: "Is it authentic or curated? Both are valid, but know the difference.",
    relatedModule: 4,
  },
  {
    id: 59,
    category: "reflection",
    title: "Grief Check",
    tip: "What friendship loss am I still carrying without realising it?",
    action: "Unprocessed grief shows up in unexpected places. Name it to heal it.",
    relatedModule: 5,
  },
  {
    id: 60,
    category: "reflection",
    title: "Future Vision",
    tip: "What kind of friend do I want to be in 5 years?",
    action: "Who you want to be guides who you are today.",
    relatedModule: 8,
  },
  {
    id: 61,
    category: "reflection",
    title: "Need vs Want",
    tip: "Do I need this friendship or just want to be needed?",
    action: "Codependency masquerades as care. Know the difference.",
    relatedModule: 2,
  },
  {
    id: 62,
    category: "reflection",
    title: "Authenticity Gauge",
    tip: "What can't I say to this friend?",
    action: "Topics you avoid reveal where trust is still building (or breaking).",
    relatedModule: 7,
  },
  {
    id: 63,
    category: "reflection",
    title: "Reciprocity Check",
    tip: "Is this friendship balanced or am I doing all the emotional labour?",
    action: "Love isn't 50/50 all the time, but it should average out.",
    relatedModule: 8,
  },
  {
    id: 64,
    category: "reflection",
    title: "Boundary Audit",
    tip: "Where am I saying yes but feeling no?",
    action: "Your resentment is trying to tell you something. Listen.",
    relatedModule: 2,
  },
  {
    id: 65,
    category: "reflection",
    title: "Growth Space",
    tip: "Does this friendship challenge me to grow or keep me stuck?",
    action: "True friends support your evolution, even when it's uncomfortable.",
    relatedModule: 6,
  },
  {
    id: 66,
    category: "reflection",
    title: "Fear Finder",
    tip: "What am I most afraid of in this friendship situation?",
    action: "Fear of rejection? Abandonment? Conflict? Name it to face it.",
    relatedModule: 1,
  },
  {
    id: 67,
    category: "reflection",
    title: "Joy Tracker",
    tip: "When was the last time this friendship brought me genuine joy?",
    action: "If you're struggling to remember, that's information.",
    relatedModule: 5,
  },
  {
    id: 68,
    category: "reflection",
    title: "Trust Check",
    tip: "Who do you feel you can be your real self with? It's fine for that to be only one or two people.",
    action: "Trust grows a bit at a time. Notice who you'd like to trust more, and what small step might help.",
    relatedModule: 7,
  },
  {
    id: 69,
    category: "reflection",
    title: "Influence Check",
    tip: "How has this friendship changed me? For better or worse?",
    action: "We become like the people we spend time with. Make sure you like the direction.",
    relatedModule: 2,
  },
  {
    id: 70,
    category: "reflection",
    title: "Wisdom Harvest",
    tip: "What has this difficult friendship taught me about myself?",
    action: "Even painful friendships can be teachers if we extract the lessons.",
    relatedModule: 8,
  },

  // Micro-Challenges (15 tips)
  {
    id: 71,
    category: "challenge",
    title: "24-Hour Challenge",
    tip: "Go 24 hours without complaining about anyone. Notice the shift.",
    action: "Breaking the negativity cycle creates space for positivity.",
    relatedModule: 2,
  },
  {
    id: 72,
    category: "challenge",
    title: "Vulnerability Dare",
    tip: "Share something you're struggling with to someone you semi-trust.",
    action: "Start small. Build the vulnerability muscle gradually.",
    relatedModule: 7,
  },
  {
    id: 73,
    category: "challenge",
    title: "Digital Sabbath",
    tip: "Choose one day this week: no social media for 24 hours.",
    action: "Notice how it feels. It might feel strange at first.",
    relatedModule: 4,
  },
  {
    id: 74,
    category: "challenge",
    title: "Repair Mission",
    tip: "Reach out to one friend you've lost touch with. No agenda, just 'thinking of you.'",
    action: "The worst they can say is nothing. Best case? Reconnection.",
    relatedModule: 6,
  },
  {
    id: 75,
    category: "challenge",
    title: "No-Defend Day",
    tip: "For one day, when criticised, just say 'Thanks for telling me' instead of defending.",
    action: "Notice how it changes the dynamic. You might be surprised.",
    relatedModule: 3,
  },
  {
    id: 76,
    category: "challenge",
    title: "Listening Lab",
    tip: "Have one conversation where you only ask questions, never share about yourself.",
    action: "It's harder than it sounds. Pure listening is a superpower.",
    relatedModule: 3,
  },
  {
    id: 77,
    category: "challenge",
    title: "Gratitude Bomb",
    tip: "Tell 3 friends specifically what you appreciate about them today.",
    action: "Be genuine and specific. Watch how it shifts your relationships.",
    relatedModule: 8,
  },
  {
    id: 78,
    category: "challenge",
    title: "Boundary Practice",
    tip: "Say no to one thing you'd normally say yes to (guilt-free).",
    action: "Notice: the world doesn't end. You're allowed to prioritise yourself.",
    relatedModule: 2,
  },
  {
    id: 79,
    category: "challenge",
    title: "Solo Activity",
    tip: "Do something on your own: a walk, a film, the library. Build some comfort with your own company.",
    action: "Being OK on your own makes it easier to choose friendships, rather than just needing them.",
    relatedModule: 7,
  },
  {
    id: 80,
    category: "challenge",
    title: "Forgiveness Work",
    tip: "Write down a friendship hurt. Then write: 'I'm putting this down.' Fold the paper up and put it away.",
    action: "Forgiveness is for you, not them. It's setting down a weight.",
    relatedModule: 5,
  },
  {
    id: 81,
    category: "challenge",
    title: "New Group",
    tip: "Attend one event where you know no one. Practice being the new person.",
    action: "Discomfort is growth. You've got this.",
    relatedModule: 6,
  },
  {
    id: 82,
    category: "challenge",
    title: "Conflict Initiation",
    tip: "Address one small thing that's been bothering you (using 'I' statements).",
    action: "Start with low stakes. Build your conflict courage gradually.",
    relatedModule: 3,
  },
  {
    id: 83,
    category: "challenge",
    title: "Phone Call Week",
    tip: "Call instead of text for all plans this week.",
    action: "Voice creates connection text can't match.",
    relatedModule: 4,
  },
  {
    id: 84,
    category: "challenge",
    title: "Assumption Check",
    tip: "Ask 'What's your story?' before jumping to conclusions about someone's behaviour.",
    action: "Most drama comes from stories we make up. Get the real story.",
    relatedModule: 3,
  },
  {
    id: 85,
    category: "challenge",
    title: "Quality Time",
    tip: "Plan one phone-free hangout this week. Full attention, no devices.",
    action: "Presence is the greatest gift you can give a friend.",
    relatedModule: 8,
  },

  // Module Reminders (15 tips)
  {
    id: 86,
    category: "reminder",
    title: "Module Wisdom",
    tip: "Being left out genuinely hurts. Treat it with the same care you'd give a physical hurt.",
    action: "Review Module 1 for the research on social pain.",
    relatedModule: 1,
  },
  {
    id: 87,
    category: "reminder",
    title: "Group Dynamics",
    tip: "People often slip into different parts in different groups. That isn't a fixed type, and it can change.",
    action: "Revisit Module 2 for what research says about how groups work.",
    relatedModule: 2,
  },
  {
    id: 88,
    category: "reminder",
    title: "Conflict Skills",
    tip: "Healthy conflict strengthens relationships. Avoidance weakens them.",
    action: "Module 3 has scripts for difficult conversations. Use them.",
    relatedModule: 3,
  },
  {
    id: 89,
    category: "reminder",
    title: "Digital Balance",
    tip: "Online drama feeds on instant reactions. Delay = drama reduction.",
    action: "Review Module 4's digital boundary strategies.",
    relatedModule: 4,
  },
  {
    id: 90,
    category: "reminder",
    title: "Endings Matter",
    tip: "How a friendship ends matters less than how you process it.",
    action: "Module 5 has tools for closure, even without their cooperation.",
    relatedModule: 5,
  },
  {
    id: 91,
    category: "reminder",
    title: "Finding Friends",
    tip: "Your people are out there doing the things you love. Go do those things.",
    action: "Module 6 shows where to find friends who match your vibe.",
    relatedModule: 6,
  },
  {
    id: 92,
    category: "reminder",
    title: "Confidence Code",
    tip: "Social confidence isn't about being fearless. It's about acting despite fear.",
    action: "Module 7 has tools to try. They only help if you use them.",
    relatedModule: 7,
  },
  {
    id: 93,
    category: "reminder",
    title: "Friendship Mastery",
    tip: "Great friendships are built, not found. They require intention and effort.",
    action: "Module 8 has the blueprint. Implementation is up to you.",
    relatedModule: 8,
  },
  {
    id: 94,
    category: "reminder",
    title: "Social Battery",
    tip: "You can't pour from an empty cup. Recharge is productive, not selfish.",
    action: "Use the Social Battery Optimizer tool if you're running on empty.",
    relatedModule: 1,
  },
  {
    id: 95,
    category: "reminder",
    title: "Unspoken Rules",
    tip: "Every group has invisible rules. Learning to read them is a superpower.",
    action: "Module 2's exercises help you decode group dynamics.",
    relatedModule: 2,
  },
  {
    id: 96,
    category: "reminder",
    title: "Repair Skills",
    tip: "Arguments happen in every close friendship. What matters most is how you come back from them.",
    action: "Module 3 teaches the art of the apology and repair.",
    relatedModule: 3,
  },
  {
    id: 97,
    category: "reminder",
    title: "Screen Wisdom",
    tip: "Your online life should support your real life, not replace it.",
    action: "Check Module 4 if the balance has tipped too far toward screens.",
    relatedModule: 4,
  },
  {
    id: 98,
    category: "reminder",
    title: "Letting Go",
    tip: "Some friendships are meant to be chapters, not the whole book.",
    action: "Module 5 gives permission to grieve and move forward.",
    relatedModule: 5,
  },
  {
    id: 99,
    category: "reminder",
    title: "Connection Quest",
    tip: "Making new friends is hard at any age. That's normal. Keep showing up.",
    action: "Module 6 has ideas to try. Give them time.",
    relatedModule: 6,
  },
  {
    id: 100,
    category: "reminder",
    title: "Inner Work",
    tip: "Your relationship with yourself sets the template for all other relationships.",
    action: "Module 7 isn't just about confidence—it's about self-compassion.",
    relatedModule: 7,
  }
];

// Helper function to get tip of the day (deterministic based on date)
export const getTipOfTheDay = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
  const tipIndex = dayOfYear % FRIENDSHIP_TIPS.length;
  return FRIENDSHIP_TIPS[tipIndex];
};

// Helper to get personalized tip based on user progress
export const getPersonalizedTip = (completedModules = [], archetype = null) => {
  // Filter tips related to completed modules
  const relevantTips = FRIENDSHIP_TIPS.filter(tip =>
    completedModules.includes(tip.relatedModule)
  );

  // If user has completed modules, show tips from those
  if (relevantTips.length > 0) {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
    const tipIndex = dayOfYear % relevantTips.length;
    return relevantTips[tipIndex];
  }

  // Otherwise, use standard tip of the day
  return getTipOfTheDay();
};

// Get tips by category
export const getTipsByCategory = (category) => {
  return FRIENDSHIP_TIPS.filter(tip => tip.category === category);
};

// Get tips by module
export const getTipsByModule = (moduleId) => {
  return FRIENDSHIP_TIPS.filter(tip => tip.relatedModule === moduleId);
};
